import {useContext, useMemo, useState} from "react";
import {useTranslation} from "react-i18next";
import emailjs from '@emailjs/browser';
import {isEmpty} from "lodash-es";
import {
    faArrowRight,
    faCheck,
    faCircleExclamation,
    faCircleInfo,
    faPaperPlane,
    faShoppingCart,
    faXmark
} from "@fortawesome/pro-solid-svg-icons";

import {Styled} from "./BasketDrawer.styles";
import Drawer from "../../../lib/UIBookBrd/Drawer";
import ActionButton from "../../../lib/UIBookBrd/ActionButton";
import Stepper from "../../../lib/UIBookBrd/Stepper";
import useBasket, {BASKET_STORAGE_KEY, defaultBasketState} from "../../../lib/Hooks/useBasket";
import useS115 from "../../../lib/Hooks/useS115";
import {Local_Data} from "../../../Context";
import {RENT_LIST_PAGE} from "../../../Routes";
import {getProductName, hasNumericPrice} from "../../../lib/products";

/** Re-exported for older imports. */
export const Basket = BASKET_STORAGE_KEY
export {defaultBasketState}

const EMAIL_SERVICE = 'service_fc1ffwk'
const EMAIL_TEMPLATE = 'template_g90eakl'
const EMAIL_PUBLIC_KEY = 'IZWjcl2cKB9IT-1K1'

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/

const FIELDS = [
    {name: 'name', labelKey: 'lbl_name_placeholder'},
    {name: 'email', labelKey: 'lbl_email_placeholder'},
    {name: 'phone', labelKey: 'lbl_phone_placeholder'},
    {name: 'date', labelKey: 'lbl_when_how_long_placeholder'},
    {name: 'additional', labelKey: 'lbl_additional_info_placeholder', isOptional: true, isMultiline: true}
]

const emptyUserInfo = {
    name: '',
    email: '',
    phone: '',
    date: '',
    additional: ''
}

const BasketDrawer = () => {

    const [localData, setLocalData] = useContext<any>(Local_Data);

    const {t} = useTranslation()

    const {navigatePage} = useS115()

    const {basketState, setBasketState, items, totalCount, addItem, removeItem, dropItem, reset} = useBasket()

    const [userInfo, setUserInfo] = useState<Record<string, string>>(emptyUserInfo)

    const [errors, setErrors] = useState<string[]>([])

    const [isSending, setIsSending] = useState<boolean>(false)

    const {Products} = localData ?? {}

    /** Basket lines that still exist in the catalogue. */
    const lines = useMemo(() => (
        Object.keys(items)
            .map((id) => ({id, count: items[id]?.count ?? 0, value: Products?.[id]}))
            .filter((line) => !!line.value && line.count > 0)
    ), [items, Products])

    const total = lines.reduce((accumulator, {value, count}) => (
        accumulator + (hasNumericPrice(value?.rentPrice) ? value.rentPrice * count : 0)
    ), 0)

    const hasOnRequestItems = lines.some(({value}) => !hasNumericPrice(value?.rentPrice))

    const onClose = () => {
        setLocalData((state: any) => ({...state, isBasketOpen: false}))
        if (basketState.sent) {
            // Fresh start for the next enquiry.
            reset()
            setUserInfo(emptyUserInfo)
        }
    }

    const onFieldChange = (name: string, nextValue: string) => {
        setUserInfo((state) => ({...state, [name]: nextValue}))
        setErrors((state) => state.filter((field) => field !== name))
    }

    const validate = () => {
        const nextErrors = FIELDS
            .filter((field) => !field.isOptional && isEmpty(userInfo[field.name]?.trim()))
            .map((field) => field.name)

        if (!nextErrors.includes('email') && !EMAIL_PATTERN.test(userInfo.email.trim())) {
            nextErrors.push('email')
        }
        return nextErrors
    }

    const sendRequest = () => {
        const nextErrors = validate()
        if (nextErrors.length) {
            setErrors(nextErrors)
            return
        }

        const products = lines
            .map(({id, count}) => `${id}: ilosc ${count}`)
            .join(', ')

        setIsSending(true)

        emailjs.send(EMAIL_SERVICE, EMAIL_TEMPLATE, {
            name: `${userInfo.name} , ${userInfo.email}`,
            message: `${userInfo.additional}`,
            phone: `${userInfo.phone}`,
            date: userInfo.date,
            products
        }, {publicKey: EMAIL_PUBLIC_KEY})
            .then(() => {
                setBasketState((state: any) => ({
                    ...state,
                    error: false,
                    sent: true,
                    items: {}
                }))
            })
            .catch(() => {
                setBasketState((state: any) => ({...state, error: true}))
            })
            .finally(() => setIsSending(false))
    }

    const title = (
        <Styled.DrawerTitle>
            {t('lbl_basket')}
            {totalCount > 0 && !basketState.sent && (
                <Styled.DrawerTitleCount>
                    {`${totalCount} ${t('lbl_pieces_short')}`}
                </Styled.DrawerTitleCount>
            )}
        </Styled.DrawerTitle>
    )

    const controls = !basketState.sent && lines.length ? (
        <Styled.Footer>
            <Styled.TotalRow>
                <Styled.TotalLabel>{t('lbl_estimated_total')}</Styled.TotalLabel>
                <Styled.TotalValue>{`${total} PLN`}</Styled.TotalValue>
                <Styled.TotalUnit>{t('lbl_rent_day')}</Styled.TotalUnit>
            </Styled.TotalRow>
            {hasOnRequestItems && (
                <Styled.RequestNote>{t('lbl_some_items_on_request')}</Styled.RequestNote>
            )}
            <ActionButton
                fullWidth={true}
                size={'lg'}
                icon={faPaperPlane}
                disabled={isSending}
                label={isSending ? t('lbl_sending') : t('lbl_send_request')}
                onClick={sendRequest}
            />
        </Styled.Footer>
    ) : undefined

    return (
        <Drawer
            isOpen={localData?.isBasketOpen ?? false}
            position={'right'}
            title={title}
            controls={controls}
            onClose={onClose}
        >
            <Styled.Content>
                {basketState.sent ? (
                    <Styled.Success
                        initial={{opacity: 0, y: 12}}
                        animate={{opacity: 1, y: 0}}
                        transition={{duration: 0.4}}
                    >
                        <Styled.SuccessRing
                            initial={{scale: 0.8}}
                            animate={{scale: 1}}
                            transition={{type: 'spring', stiffness: 320, damping: 18}}
                        >
                            <Styled.SuccessIcon icon={faCheck}/>
                        </Styled.SuccessRing>
                        <Styled.SuccessTitle>{t('lbl_basket_success_email')}</Styled.SuccessTitle>
                        <Styled.SuccessText>{t('lbl_basket_success_desc')}</Styled.SuccessText>
                        <ActionButton
                            variant={'outline'}
                            label={t('lbl_back_to_offer')}
                            iconRight={faArrowRight}
                            onClick={() => {
                                onClose()
                                navigatePage(RENT_LIST_PAGE)
                            }}
                        />
                    </Styled.Success>
                ) : !lines.length ? (
                    <Styled.Empty>
                        <Styled.EmptyIconRing>
                            <Styled.EmptyIcon icon={faShoppingCart}/>
                        </Styled.EmptyIconRing>
                        <Styled.EmptyTitle>{t('lbl_empty_basket_message')}</Styled.EmptyTitle>
                        <Styled.EmptyText>{t('lbl_empty_basket_desc')}</Styled.EmptyText>
                        <ActionButton
                            label={t('lbl_browse_equipment')}
                            iconRight={faArrowRight}
                            onClick={() => {
                                onClose()
                                navigatePage(RENT_LIST_PAGE)
                            }}
                        />
                    </Styled.Empty>
                ) : (
                    <>
                        <Styled.Items>
                            {lines.map(({id, value, count}) => (
                                <Styled.Item
                                    key={id}
                                    layout={true}
                                    initial={{opacity: 0, y: 8}}
                                    animate={{opacity: 1, y: 0}}
                                    transition={{duration: 0.3}}
                                >
                                    <Styled.ItemImage
                                        src={value?.image}
                                        alt={id}
                                        fit={'contain'}
                                    />
                                    <Styled.ItemBody>
                                        <Styled.ItemProducer>{value?.producent ?? 'S115'}</Styled.ItemProducer>
                                        <Styled.ItemName>{getProductName(id, value, t)}</Styled.ItemName>
                                        <Styled.ItemUnit>
                                            {hasNumericPrice(value?.rentPrice)
                                                ? `${value.rentPrice} PLN ${t('lbl_rent_day')}`
                                                : t('lbl_price_on_request')}
                                        </Styled.ItemUnit>
                                        <Styled.ItemBottom>
                                            <Stepper
                                                size={'sm'}
                                                value={count}
                                                max={value?.count}
                                                unit={t('lbl_pieces_short')}
                                                removeAtMin={true}
                                                decreaseLabel={t('lbl_remove')}
                                                increaseLabel={t('lbl_add_to_basket')}
                                                onDecrease={() => removeItem(id)}
                                                onIncrease={() => addItem(id, value?.count)}
                                            />
                                            {hasNumericPrice(value?.rentPrice) && (
                                                <Styled.ItemTotal>
                                                    {`${value.rentPrice * count} PLN`}
                                                </Styled.ItemTotal>
                                            )}
                                        </Styled.ItemBottom>
                                    </Styled.ItemBody>
                                    <Styled.RemoveButton
                                        onClick={() => dropItem(id)}
                                        aria-label={t('lbl_remove')}
                                    >
                                        <Styled.RemoveIcon icon={faXmark}/>
                                    </Styled.RemoveButton>
                                </Styled.Item>
                            ))}
                        </Styled.Items>

                        <Styled.Form>
                            <Styled.FormHead>
                                <Styled.FormTitle>{t('lbl_your_details')}</Styled.FormTitle>
                                <Styled.FormLead>{t('lbl_your_details_lead')}</Styled.FormLead>
                            </Styled.FormHead>

                            {FIELDS.map((field) => {
                                const hasError = errors.includes(field.name)
                                return (
                                    <Styled.Field key={field.name}>
                                        <Styled.FieldLabel>
                                            {`${t(field.labelKey)}${field.isOptional ? '' : ' *'}`}
                                        </Styled.FieldLabel>
                                        {field.isMultiline ? (
                                            <Styled.TextArea
                                                $error={hasError}
                                                value={userInfo[field.name]}
                                                placeholder={t(field.labelKey)}
                                                onChange={(event: React.ChangeEvent<HTMLTextAreaElement>) => onFieldChange(field.name, event.target.value)}
                                            />
                                        ) : (
                                            <Styled.Input
                                                $error={hasError}
                                                type={field.name === 'email' ? 'email' : 'text'}
                                                inputMode={field.name === 'phone' ? 'tel' : undefined}
                                                value={userInfo[field.name]}
                                                placeholder={t(field.labelKey)}
                                                onChange={(event: React.ChangeEvent<HTMLInputElement>) => onFieldChange(field.name, event.target.value)}
                                            />
                                        )}
                                        {hasError && (
                                            <Styled.FieldError>
                                                <Styled.FieldErrorIcon icon={faCircleExclamation}/>
                                                {field.name === 'email' && !isEmpty(userInfo.email.trim())
                                                    ? t('lbl_invalid_email')
                                                    : t('lbl_required_field')}
                                            </Styled.FieldError>
                                        )}
                                    </Styled.Field>
                                )
                            })}

                            {basketState.error && (
                                <Styled.Alert>
                                    <Styled.FieldErrorIcon icon={faCircleExclamation}/>
                                    {t('lbl_basket_error_email')}
                                </Styled.Alert>
                            )}

                            <Styled.Disclaimer>
                                <Styled.DisclaimerSummary>
                                    <Styled.DisclaimerIcon icon={faCircleInfo}/>
                                    {t('lbl_legal_note')}
                                </Styled.DisclaimerSummary>
                                <Styled.DisclaimerText>
                                    {t('lbl_basket_disclaimer')}
                                </Styled.DisclaimerText>
                            </Styled.Disclaimer>
                        </Styled.Form>
                    </>
                )}
            </Styled.Content>
        </Drawer>
    )
}

export default BasketDrawer
